import { useCallback, useEffect, useId, useRef } from 'react';
import { reportSyncCounts } from '../utils/syncQueueStore';

const AUTO_RETRY_DELAY_MS = 2000;
const MAX_AUTO_RETRIES = 1;

/**
 * Background-sync engine for one editor instance. API-agnostic: the caller
 * supplies the actual create/update/delete run functions; this hook owns
 * per-item ordering, create->update/delete deferral, the single silent
 * auto-retry, and reporting pending/error counts to the shared store.
 *
 * @param {string} scopeKey  e.g. `plan-42` or `template-7`
 * @param {string} periodName the meal/workout group this instance owns
 */
export function useBackgroundSync(scopeKey, periodName) {
  const chains = useRef(new Map()); // localId -> promise chain (per-item ordering)
  const serverIds = useRef(new Map()); // localId -> real server id once known
  const pendingOps = useRef(new Map()); // localId -> { type: 'update'|'delete', payload? } deferred until create resolves
  const retryCounts = useRef(new Map()); // localId -> auto-retries already spent
  const inFlightOps = useRef(0); // ops enqueued (any item) that haven't settled yet
  const onIdleRef = useRef(null);
  // One store entry per hook instance, never per (scope, period): the templates
  // meal-types-first view mounts one editor per option, all bound to the same
  // meal period and scope. Sharing a key makes siblings overwrite each other —
  // the aggregate goes last-writer-wins, and because each write is a real change
  // relative to what the sibling just stored, the store publishes on every pass.
  // Publishing re-renders every subscriber, and every subscriber is an ancestor
  // of the editors reporting, so the reports never settle and React eventually
  // throws "Maximum update depth exceeded". usePendingSyncTotal aggregates on
  // the `${scopeKey}:` prefix, so the extra segment is invisible to it.
  const instanceId = useId();
  const periodKey = `${scopeKey}:${periodName}#${instanceId}`;

  // The key is an instance detail, so its lifetime is too: release this
  // instance's counts on unmount rather than making every caller do it.
  useEffect(
    () => () => reportSyncCounts(periodKey, { pending: 0, error: 0 }),
    [periodKey]
  );

  // Called once, when every queued op across every item has settled. This is
  // the ONLY point a refetch should happen: each item keeps its own stack of
  // operations, and the fetch waits for all stacks to drain.
  const setOnIdle = useCallback((fn) => {
    onIdleRef.current = fn;
  }, []);

  const reportCounts = useCallback(
    (items) => {
      let pending = 0;
      let error = 0;
      items.forEach((it) => {
        const status = it._sync?.status;
        if (status === 'syncing' || status === 'deleting') pending += 1;
        else if (status === 'error') error += 1;
      });
      reportSyncCounts(periodKey, { pending, error });
    },
    [periodKey]
  );

  // Runs taskFn only after any prior queued task for this localId settles,
  // preserving the order the user triggered create -> update -> delete.
  // The in-flight counter spans every item's chain; when it returns to zero
  // the idle callback fires so the caller can do its single final fetch.
  // Counting happens at enqueue time (not execution time) so an op that
  // enqueues a follow-up before returning can never let the count dip to zero
  // in between.
  const enqueue = useCallback((localId, taskFn) => {
    const prior = chains.current.get(localId) || Promise.resolve();
    inFlightOps.current += 1;
    const next = prior.catch(() => {}).then(taskFn);
    const settled = next.catch(() => {}).then(() => {
      inFlightOps.current -= 1;
      if (inFlightOps.current === 0) onIdleRef.current?.();
    });
    chains.current.set(localId, settled);
    return next;
  }, []);

  // True while the current attempt is NOT the last one, so run functions can
  // stay 'syncing' (no visible error) during the silent retry.
  const willAutoRetry = useCallback(
    (localId) => (retryCounts.current.get(localId) || 0) < MAX_AUTO_RETRIES,
    []
  );

  const withAutoRetry = useCallback(
    (localId, runFn) =>
      async function attempt(...args) {
        try {
          await runFn(...args);
        } catch (err) {
          const spent = retryCounts.current.get(localId) || 0;
          if (spent < MAX_AUTO_RETRIES) {
            retryCounts.current.set(localId, spent + 1);
            await new Promise((r) => setTimeout(r, AUTO_RETRY_DELAY_MS));
            return attempt(...args); // one silent retry, then give up to manual retry
          }
          // Final failure: the run function has already marked the item 'error'.
        }
      },
    []
  );

  const resetRetries = useCallback((localId) => {
    retryCounts.current.delete(localId);
  }, []);

  const clearItem = useCallback((localId) => {
    chains.current.delete(localId);
    serverIds.current.delete(localId);
    pendingOps.current.delete(localId);
    retryCounts.current.delete(localId);
  }, []);

  return {
    enqueue,
    setOnIdle,
    withAutoRetry,
    willAutoRetry,
    resetRetries,
    clearItem,
    chains,
    serverIds,
    pendingOps,
    retryCounts,
    reportCounts,
  };
}
